/**
 * EDITOR-JUMP-RULE.MODAL
 * Select a target question to jump to when a QCM proposition is chosen
 */
import { useEffect, useRef } from "react"
import { withTranslation, WithTranslation } from "react-i18next"
import { connect } from "react-redux"
import { flatten } from "lodash"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowRight, faCheck } from "@fortawesome/free-solid-svg-icons"
import Modal from "@/components/modal"
import Button from "@/components/button"
import { store } from "@/index"
import { templateEditAndUpdate } from "@/redux/template.actions"
import { TemplateState } from "@/redux/template.types"
import { TopicState } from "@/redux/topic.types"
import { Proposition } from "@/redux/proposition.types"
import { Session } from "@/redux/_session.types"

interface StateProps extends WithTranslation {
  _session: Session
  template: TemplateState
  topic: TopicState
}

interface OwnProps {
  proposition: Proposition
  questionId: string
  onClose: Function
}

type Props = StateProps & OwnProps

function EditorJumpRuleModal(props: Props) {
  const { t } = props

  const jumpRules = props.template.active.options.jumpRules || []
  const currentRule = jumpRules.find(r => r.id === props.proposition.id) || null

  const questions = flatten(props.topic.list.map(tp => tp.Questions)).filter(
    q => q.id !== props.questionId && !q.primaryId
  )

  const activeRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (activeRef.current) {
      activeRef.current.scrollIntoView({ block: "center", behavior: "smooth" })
    }
  }, [])

  function selectTarget(questionId: string | null) {
    const updated =
      questionId === null
        ? jumpRules.filter(r => r.id !== props.proposition.id)
        : [...jumpRules.filter(r => r.id !== props.proposition.id), { id: props.proposition.id, type: "proposition" as const, jumpToTopic: questionId }]
    store.dispatch(templateEditAndUpdate("options", { ...props.template.active.options, jumpRules: updated }))
    props.onClose()
  }

  return (
    <Modal
      disableClickOutside
      isCloseButtonVisible
      onClose={() => props.onClose()}
      title={t("proposition_jump_logic")}
      footer={currentRule && (
        <Button color="#e05252" onClick={() => selectTarget(null)}>
          {t("proposition_jump_remove")}
        </Button>
      )}>

      <p className="grey-t" style={{ fontSize: 13, marginBottom: 12 }}>
        {t("proposition_jump_if")} <b>{props.proposition.name[props.template.active.language]}</b>{t("proposition_jump_if_then")}
      </p>

      <div style={{ maxHeight: 320, overflowY: "auto" }}>
        {questions.map(q => (
          <div
            key={q.id}
            ref={currentRule?.jumpToTopic === q.id ? activeRef : null}
            className="_hover"
            style={{ padding: "4px 12px", fontSize: 13, cursor: "pointer" }}
            onClick={() => selectTarget(q.id)}>
            {currentRule?.jumpToTopic === q.id ? (
              <div
                className="dropdown-item-active flex"
                style={{ color: props._session.accountColors.active, justifyContent: "flex-start", alignItems: "center" }}>
                <FontAwesomeIcon icon={faCheck} style={{ width: 12, height: 12, flexShrink: 0, margin: 0, marginRight: 8 }} />
                {q.name[props.template.active.language] || t("question_new")}
              </div>
            ) : (
              <div className="flex" style={{ alignItems: "center", gap: 8 }}>
                <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: 10, color: "#8c8c8c", flexShrink: 0 }} />
                {q.name[props.template.active.language] || t("question_new")}
              </div>
            )}
          </div>
        ))}
      </div>

    </Modal>
  )
}

const mapStateToProps = state => ({
  _session: state._session,
  template: state.template,
  topic: state.topic
})

export default connect(mapStateToProps)(withTranslation()(EditorJumpRuleModal))
